const OFFER_JSON_SCHEMA = "alotb-commercial-offer";
const OFFER_JSON_VERSION = 3;
const MAX_ITEMS = 500;

const isObject = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const text = (value, fallback = "") => {
  if (value === undefined || value === null) return fallback;
  return String(value).trim();
};

const number = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
};

const boolean = (value, fallback = false) =>
  typeof value === "boolean" ? value : fallback;

const object = (value) => (isObject(value) ? value : {});
const array = (value) => (Array.isArray(value) ? value : []);

const normalizeDate = (value) => {
  const source = text(value);
  if (!source) return new Date().toISOString().slice(0, 10);

  const match = source.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[1]}-${match[2]}-${match[3]}`;

  const parsed = new Date(source);
  return Number.isNaN(parsed.getTime())
    ? new Date().toISOString().slice(0, 10)
    : parsed.toISOString().slice(0, 10);
};

const isObjectId = (value) => /^[a-f\d]{24}$/i.test(text(value));

const normalizeCatalogReference = (source, fallbackProductId = "") => {
  const catalog = object(source);
  const productId = text(
    catalog.productId || catalog.id || fallbackProductId
  );

  return {
    productId,
    slug: text(catalog.slug),
    article: text(catalog.article || catalog.sku),
    query: text(catalog.query || catalog.search || catalog.name),
    attachLater:
      typeof catalog.attachLater === "boolean"
        ? catalog.attachLater
        : !isObjectId(productId),
  };
};

const normalizeImportedItem = (source, index) => {
  const item = object(source);
  const itemMeta = object(item.meta);
  const media = object(item.media);
  const catalogReference = normalizeCatalogReference(
    item.catalogProduct || item.product || itemMeta.catalogProduct,
    item.productId
  );

  const productId = isObjectId(catalogReference.productId)
    ? catalogReference.productId
    : undefined;
  const imageUrl = text(media.imageUrl || item.imageUrl) || null;
  const productUrl = text(
    media.productUrl || itemMeta.productUrl || item.productUrl
  );
  const sceneUrl = text(
    media.sceneUrl || itemMeta.sceneUrl || item.sceneUrl
  );

  const includeImage =
    typeof media.includeImage === "boolean"
      ? media.includeImage
      : typeof itemMeta.showImage === "boolean"
        ? itemMeta.showImage
        : Boolean(imageUrl);
  const includeProductLink =
    typeof media.includeProductLink === "boolean"
      ? media.includeProductLink
      : typeof itemMeta.showProductLink === "boolean"
        ? itemMeta.showProductLink
        : Boolean(productUrl);
  const includeSceneLink =
    typeof media.includeSceneLink === "boolean"
      ? media.includeSceneLink
      : typeof itemMeta.showSceneLink === "boolean"
        ? itemMeta.showSceneLink
        : Boolean(sceneUrl);

  return {
    ...(productId ? { productId } : {}),
    name: text(item.name || item.title, `Позиция ${index + 1}`),
    qty: number(item.quantity ?? item.qty, 1),
    price: number(item.unitPrice ?? item.price, 0),
    sum: 0,
    imageUrl,
    meta: {
      ...itemMeta,
      unit: text(item.unit || itemMeta.unit, "шт."),
      details: text(
        item.description || item.details || itemMeta.details
      ),
      dimensions: text(item.dimensions || itemMeta.dimensions),
      showImage: includeImage,
      productUrl,
      showProductLink: includeProductLink,
      sceneUrl,
      showSceneLink: includeSceneLink,
      catalogProduct: catalogReference,
    },
  };
};

const normalizeModernDocument = (source) => {
  const document = object(source.document);
  const seller = object(source.seller);
  const customer = object(source.customer);
  const terms = object(source.terms);
  const presentation = object(source.presentation);
  const recipient = object(presentation.recipient);
  const sender = object(presentation.sender);
  const signature = object(presentation.signature);
  const pricing = object(source.pricing);
  const items = array(source.items);

  if (items.length > MAX_ITEMS) {
    throw new Error(`В JSON больше ${MAX_ITEMS} позиций.`);
  }

  return {
    schemaVersion: OFFER_JSON_VERSION,
    docNumber: text(document.number || document.docNumber),
    date: normalizeDate(document.date),
    currency: text(document.currency, "RUB").toUpperCase(),
    title: text(document.title, "Коммерческое предложение"),
    company: text(seller.company || seller.name, "ALOTB.RU"),
    description: text(document.description || document.subject),
    client: {
      name: text(customer.name || customer.company),
      phone: text(customer.phone),
      email: text(customer.email),
      address: text(customer.address),
      extra: text(customer.extra),
    },
    meta: {
      delivery: text(terms.delivery || terms.deadline),
      payment: text(terms.payment),
      warranty: text(terms.warranty),
      validity: text(terms.validity),
      extra: text(terms.extra || terms.notes),
      managerName: text(terms.managerName || seller.managerName),
      managerPhone: text(terms.managerPhone || seller.managerPhone),
      managerEmail: text(terms.managerEmail || seller.managerEmail),
      documentHeading: text(
        document.heading || presentation.documentHeading,
        "КОММЕРЧЕСКОЕ ПРЕДЛОЖЕНИЕ"
      ),
      documentDateText: text(
        document.dateText || presentation.documentDateText
      ),
      showRecipient: boolean(
        recipient.enabled ?? presentation.showRecipient,
        false
      ),
      recipientLabel: text(
        recipient.label || presentation.recipientLabel,
        "Кому"
      ),
      recipientText: text(
        recipient.text || presentation.recipientText
      ),
      showSender: boolean(
        sender.enabled ?? presentation.showSender,
        false
      ),
      senderLabel: text(
        sender.label || presentation.senderLabel,
        "От"
      ),
      senderText: text(sender.text || presentation.senderText),
      showSignature: boolean(
        signature.enabled ?? presentation.showSignature,
        false
      ),
      signatureLabel: text(
        signature.label || presentation.signatureLabel,
        "Подпись"
      ),
      signaturePosition: text(
        signature.position || presentation.signaturePosition
      ),
      signatureName: text(
        signature.name || presentation.signatureName
      ),
    },
    items: items.map(normalizeImportedItem),
    totals: {
      discount: number(pricing.discount, 0),
      vat: number(pricing.tax ?? pricing.vat, 0),
    },
    status: "draft",
  };
};

const normalizeLegacyDocument = (source) => {
  const offer = object(source.offer || source);
  const items = array(offer.items);

  if (items.length > MAX_ITEMS) {
    throw new Error(`В JSON больше ${MAX_ITEMS} позиций.`);
  }

  return {
    schemaVersion: OFFER_JSON_VERSION,
    docNumber: text(offer.docNumber),
    date: normalizeDate(offer.date),
    currency: text(offer.currency, "RUB").toUpperCase(),
    title: text(offer.title, "Коммерческое предложение"),
    company: text(offer.company, "ALOTB.RU"),
    description: text(offer.description),
    client: {
      ...object(offer.client),
      name: text(offer.client?.name),
      phone: text(offer.client?.phone),
      email: text(offer.client?.email),
      address: text(offer.client?.address),
      extra: text(offer.client?.extra),
    },
    meta: {
      ...object(offer.meta),
      delivery: text(offer.meta?.delivery),
      payment: text(offer.meta?.payment),
      warranty: text(offer.meta?.warranty),
      validity: text(offer.meta?.validity),
      extra: text(offer.meta?.extra),
      documentHeading: text(
        offer.meta?.documentHeading,
        "КОММЕРЧЕСКОЕ ПРЕДЛОЖЕНИЕ"
      ),
      documentDateText: text(offer.meta?.documentDateText),
      showRecipient: boolean(offer.meta?.showRecipient, false),
      recipientLabel: text(offer.meta?.recipientLabel, "Кому"),
      recipientText: text(offer.meta?.recipientText),
      showSender: boolean(offer.meta?.showSender, false),
      senderLabel: text(offer.meta?.senderLabel, "От"),
      senderText: text(offer.meta?.senderText),
      showSignature: boolean(offer.meta?.showSignature, false),
      signatureLabel: text(offer.meta?.signatureLabel, "Подпись"),
      signaturePosition: text(offer.meta?.signaturePosition),
      signatureName: text(offer.meta?.signatureName),
    },
    items: items.map(normalizeImportedItem),
    totals: {
      discount: number(offer.totals?.discount, 0),
      vat: number(offer.totals?.vat, 0),
    },
    status: "draft",
  };
};

export const parseOfferJson = (rawText) => {
  const sourceText = String(rawText || "").replace(/^\uFEFF/, "").trim();
  if (!sourceText) throw new Error("JSON-файл пуст.");

  let parsed;
  try {
    parsed = JSON.parse(sourceText);
  } catch (error) {
    throw new Error(`JSON повреждён: ${error.message}`);
  }

  if (!isObject(parsed)) {
    throw new Error("Корнем JSON должен быть объект.");
  }

  const schema = text(parsed.schema);
  if (schema && schema !== OFFER_JSON_SCHEMA) {
    throw new Error(`Неподдерживаемая схема JSON: ${schema}.`);
  }

  const normalized = parsed.document
    ? normalizeModernDocument(parsed)
    : normalizeLegacyDocument(parsed);

  if (!Array.isArray(normalized.items)) {
    throw new Error("В JSON отсутствует массив items.");
  }

  return normalized;
};

export const buildOfferJson = (offer) => ({
  schema: OFFER_JSON_SCHEMA,
  schemaVersion: OFFER_JSON_VERSION,
  exportedAt: new Date().toISOString(),
  document: {
    number: text(offer?.docNumber),
    date: normalizeDate(offer?.date),
    currency: text(offer?.currency, "RUB").toUpperCase(),
    title: text(offer?.title, "Коммерческое предложение"),
    description: text(offer?.description),
    heading: text(
      offer?.meta?.documentHeading,
      "КОММЕРЧЕСКОЕ ПРЕДЛОЖЕНИЕ"
    ),
    dateText: text(offer?.meta?.documentDateText),
  },
  seller: {
    company: text(offer?.company, "ALOTB.RU"),
    managerName: text(offer?.meta?.managerName),
    managerPhone: text(offer?.meta?.managerPhone),
    managerEmail: text(offer?.meta?.managerEmail),
  },
  customer: {
    name: text(offer?.client?.name),
    phone: text(offer?.client?.phone),
    email: text(offer?.client?.email),
    address: text(offer?.client?.address),
    extra: text(offer?.client?.extra),
  },
  presentation: {
    recipient: {
      enabled: boolean(offer?.meta?.showRecipient, false),
      label: text(offer?.meta?.recipientLabel, "Кому"),
      text: text(offer?.meta?.recipientText),
    },
    sender: {
      enabled: boolean(offer?.meta?.showSender, false),
      label: text(offer?.meta?.senderLabel, "От"),
      text: text(offer?.meta?.senderText),
    },
    signature: {
      enabled: boolean(offer?.meta?.showSignature, false),
      label: text(offer?.meta?.signatureLabel, "Подпись"),
      position: text(offer?.meta?.signaturePosition),
      name: text(offer?.meta?.signatureName),
    },
  },
  terms: {
    delivery: text(offer?.meta?.delivery),
    payment: text(offer?.meta?.payment),
    warranty: text(offer?.meta?.warranty),
    validity: text(offer?.meta?.validity),
    extra: text(offer?.meta?.extra),
  },
  items: array(offer?.items).map((item) => {
    const catalogProduct = normalizeCatalogReference(
      item?.meta?.catalogProduct,
      item?.productId
    );

    return {
      name: text(item?.name),
      description: text(item?.meta?.details),
      dimensions: text(item?.meta?.dimensions),
      quantity: number(item?.qty, 0),
      unit: text(item?.meta?.unit, "шт."),
      unitPrice: number(item?.price, 0),
      catalogProduct,
      media: {
        includeImage: boolean(item?.meta?.showImage, Boolean(item?.imageUrl)),
        imageUrl: text(item?.imageUrl),
        includeProductLink: boolean(
          item?.meta?.showProductLink,
          Boolean(item?.meta?.productUrl)
        ),
        productUrl: text(item?.meta?.productUrl),
        includeSceneLink: boolean(
          item?.meta?.showSceneLink,
          Boolean(item?.meta?.sceneUrl)
        ),
        sceneUrl: text(item?.meta?.sceneUrl),
      },
    };
  }),
  pricing: {
    discount: number(offer?.totals?.discount, 0),
    tax: number(offer?.totals?.vat, 0),
  },
});

export const makeOfferJsonFilename = (offer) => {
  const raw = text(offer?.docNumber || offer?.title, "commercial-offer");
  const safe = raw
    .toLowerCase()
    .replace(/[^a-zа-яё0-9_-]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

  return `${safe || "commercial-offer"}.json`;
};

export { OFFER_JSON_SCHEMA, OFFER_JSON_VERSION };
